import React, { useMemo } from "react";
import db from "../../../constants/database";
// import { Carousel } from "react-responsive-carousel";
import SliderCharity from "./SliderCharity";

const SectionTitle: React.FC<{
  kicker?: string;
  title: string;
  subtitle?: string;
  className?: string;
}> = ({ kicker, title, subtitle, className }) => (
  <header className={`max-w-3xl mb-10 ${className}`}>
    {kicker && (
      <p className="uppercase tracking-widest text-xs text-sky-500 font-semibold mb-2">
        {kicker}
      </p>
    )}
    <h2 className={`text-3xl md:text-5xl`}>{title}</h2>
    {subtitle && (
      <p className="text-lg md:text-[24px] mt-3 text-slate-600 dark:text-slate-300">
        {subtitle}
      </p>
    )}
  </header>
);

const Card: React.FC<React.PropsWithChildren<{ className?: string }>> = ({
  className,
  children,
}) => (
  <div
    className={
      "rounded-lg shadow-sm ring-1 ring-black/5 bg-white/90 dark:bg-slate-900/80 backdrop-blur " +
      (className ?? "")
    }
  >
    {children}
  </div>
);
interface Slide {
  id: string;
  image: any;
  alt: string;
}

const Button: React.FC<
  React.PropsWithChildren<{
    as?: "button" | "a";
    href?: string;
    onClick?: () => void;
  }>
> = ({ as = "button", href, onClick, children }) => {
  const base =
    "font-abel inline-flex bg-[#243677] items-center justify-center gap-2 rounded-xl px-4 py-3 text-lg font-medium ring-1 ring-sky-600/20 hover:ring-sky-600/40 focus:outline-none focus:ring-2 focus:ring-sky-600 transition";
  const style = "bg-sky-600 text-white hover:bg-sky-700";
  if (as === "a" && href)
    return (
      <a href={href} className={`${base} ${style}`}>
        {children} <ArrowRight />
      </a>
    );
  return (
    <button onClick={onClick} className={`${base} ${style}`}>
      {children} <ArrowRight />
    </button>
  );
};

const ArrowRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className="w-4 h-4"
    aria-hidden
  >
    <path d="M13.293 4.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 11-1.414-1.414L17.586 12l-4.293-4.293a1 1 0 010-1.414z" />
    <path d="M3 12a1 1 0 011-1h14a1 1 0 110 2H4a1 1 0 01-1-1z" />
  </svg>
);

// Step card for "How it works"
const StepCard: React.FC<{ step: number; title: string; body: string }> = ({
  step,
  title,
  body,
}) => (
  <Card className="px-4 md:px-5 pb-3 md:pb-4 w-full bg-[white]">
    <div className="text-[13px] tracking-widest text-[white] bg-[#0B60D6] w-[73px] h-[26px] rounded-2xl flex justify-center items-center px-2 py-1 mb-3 -mt-4 -ml-2">
      STEP {step}
    </div>
    <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1">
      {title}
    </h3>
    <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
      {body}
    </p>
  </Card>
);

// Fund distribution card (85% / 15%)
const DistributionCard: React.FC = () => (
  <Card className="font-abel p-6 md:p-8 max-w-[678px] mx-auto relative overflow-hidden bg-gradient-to-br from-[#243677] via-[#243677] to-[#243677] text-white shadow-xl mb-5 md:mb-10">
    <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
    <h3 className="text-center text-[white] text-lg md:text-3xl tracking-wide mb-6">
      Fund Distribution
    </h3>
    <div className="grid grid-cols-2 gap-4 max-w-[528px] mx-auto bg-[#2D428F]">
      <div className="rounded-xl p-6 text-center">
        <p className="text-5xl md:text-6xl font-extrabold leading-none text-[#14A4FF]">
          85%
        </p>
        <p className="mt-2 text-sm tracking-wider text-[white]">
          Goes directly to the cause
        </p>
      </div>
      <div className="rounded-xl p-6 text-center">
        <p className="text-5xl md:text-6xl font-extrabold leading-none text-[#ADDDFE]">
          15%
        </p>
        <p className="mt-2 text-sm tracking-wider text-[white]">
          Covers administrative and operational costs
        </p>
      </div>
    </div>
    <p className="mt-6 text-[14px] text-center tracking-widest text-white/70">
      100% Transparency. 100% Integrity. 100% Accountability.
    </p>
  </Card>
);

// Commitment blurb card
const CommitmentCard: React.FC<{ title: string; body: string }> = ({
  title,
  body,
}) => (
  <Card className="p-4 md:p-5 bg-[#CCE1FF] border-l-[10px] border-[#243677] ">
    <h4 className="text-lg md:text-[22px] font-semibold text-slate-900 dark:text-slate-100 mb-2">
      {title}
    </h4>
    <p className="text-sm md:text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
      {body}
    </p>
  </Card>
);

// Modal for “Learn More” (optional action hook)
// const Modal: React.FC<{
//   open: boolean;
//   onClose: () => void;
//   title: string;
//   children: any;
// }> = ({ open, onClose, title, children }) => {
//   if (!open) return null;
//   return (
//     <div className="fixed inset-0 z-50">
//       <div className="absolute inset-0 bg-black/50" onClick={onClose} />
//       <div className="absolute inset-0 flex items-center justify-center p-4">
//         <div className="w-full max-w-2xl bg-white dark:bg-slate-900 rounded-2xl shadow-2xl ring-1 ring-black/5">
//           <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200/60 dark:border-slate-700/60">
//             <h3 className="text-lg font-semibold">{title}</h3>
//             <button
//               onClick={onClose}
//               aria-label="Close"
//               className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
//             >
//               ✕
//             </button>
//           </div>
//           <div className="px-6 py-5 text-slate-600 dark:text-slate-300">
//             {children}
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// Main page
const DonationLanding: React.FC<{
  onDonate?: (fundType: string) => void;
}> = ({ onDonate }) => {
  // const [open, setOpen] = useState(false);
  const slides: Slide[] = useMemo(
    () => [
      {
        id: "s1",
        image: db.charity,
        alt: "25th",
      },
      {
        id: "s2",
        image: db.charity2,
        alt: "give",
      },
      {
        id: "s4",
        image: db.slider1,
        alt: "give",
      },
    ],
    []
  );

  const steps = [
    {
      title: "Choose Your Cause",
      body: "General Fund, Kinabuti Initiative, or Living Foundation Orphanage.",
    },
    {
      title: "Make Your Contribution",
      body: "All donations go into one secure account.",
    },
    {
      title: "We Distribute Funds",
      body: "Based on your selection, your donation is sent directly to the chosen fund.",
    },
    {
      title: "Receive Your Tax Certificate",
      body: "At year-end, we’ll send an official donation certificate for your records.",
    },
  ];

  return (
    <main className="font-abel min-h-screen bg-slate-50 dark:bg-slate-950 text-slate-800 dark:text-slate-100">
      {/* Top banner with subtle backdrop */}
      <section className="relative isolate">
        <div className="absolute inset-0 bg-[#232323]/90 -z-10">
          <div className="h-[100%] w-full bg-kidsBg bg-cover bg-center bg-no-repeat" />
        </div>
        <div className="container mx-auto px-4 pt-12 pb-6">
          <div className="text-center">
            <SectionTitle
              title="How it Works"
              className="text-[white] mx-auto"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 xl:grid-cols-4 gap-4 md:gap-6">
            {steps.map((s, i) => (
              <StepCard key={i} step={i + 1} title={s.title} body={s.body} />
            ))}
          </div>

          <div className="max-w-3xl mx-auto mt-10 md:mt-16">
            <DistributionCard />
          </div>
        </div>
      </section>

      {/* Commitment section */}
      <section className="relative py-12 md:py-16">
        <div className="container mx-auto px-4">
          <SectionTitle title="Our Commitment to Accountability" />
          <div className="grid lg:grid-cols-2 md:grid-cols-2 gap-4 items-center">
            <div>
              <div className="space-y-4">
                <CommitmentCard
                  title="Transparent Partnerships"
                  body="We partner only with organizations that share our values of honesty, transparency, and measurable impact."
                />
                <CommitmentCard
                  title="Rigorous Oversight"
                  body="If any foundation fails to uphold these standards, 25th & Staffing will investigate immediately and may suspend or terminate their participation in the program."
                />
              </div>
            </div>
            <div className="h-[100%]">
              <SliderCharity slides={slides} />
            </div>
          </div>
        </div>
      </section>

      {/* Give today CTA */}
      <section className="relative py-12 md:py-16">
        <div className="absolute inset-0 -z-10 bg-helpBg bg-cover bg-center" />
        <div className="container mx-auto px-4">
          <div className="text-center">
            <SectionTitle
              title="Give Today"
              subtitle="Your choice. Your impact. Your legacy."
              className="mx-auto"
            />
          </div>
          <div className="mx-auto max-w-[600px] grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            <Button onClick={() => onDonate && onDonate("GENERAL_IMPACT")}>
              Give to General Impact Fund
            </Button>
            <Button onClick={() => onDonate && onDonate("KINABUTI")}>
              Support the Kinabuti Initiative
            </Button>
            {/* <Card className="p-5 flex items-center justify-between">
              <Button as="a" href="#">
                Donate
              </Button>
            </Card> */}
          </div>
        </div>
      </section>

      {/* Learn more modal */}
      {/* <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="About our fund distribution"
      >
        <p className="mb-3">
          We allocate funds based on audited quarterly reports and predefined
          caps for administrative expenses. Our target is to keep admin &
          operational costs at or below 15% while sending at least 85% directly
          to chosen causes.
        </p>
        <p>
          Detailed statements are published annually with independent review
          summaries to ensure transparency and accountability.
        </p>
      </Modal> */}
    </main>
  );
};

export default DonationLanding;
